import React, { useEffect, useId, useState } from 'react';
import { PoundSterling, Save } from 'lucide-react';
import { getMonthName } from '../utils/dateUtils';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { Field } from './ui/field';
import { Input } from './ui/input';
import { useGetMonthlyRateQuery } from '../hooks/useMonthlyRatesQuery';

interface MonthlyRateFormProps {
  year: number;
  month: number;
  onSave: (rate: number) => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

export const MonthlyRateForm: React.FC<MonthlyRateFormProps> = ({
  year,
  month,
  onSave,
  isOpen,
  onClose
}) => {
  const formId = useId();
  const { data: monthlyRate } = useGetMonthlyRateQuery(year, month);
  const [hourlyRate, setHourlyRate] = useState(monthlyRate?.hourlyRate?.toString() || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill with the saved rate each time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setHourlyRate(monthlyRate?.hourlyRate?.toString() || '');
      setError(null);
    }
  }, [isOpen, monthlyRate?.hourlyRate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const rate = parseFloat(hourlyRate);
    if (isNaN(rate) || rate < 0) {
      setError('Please enter a valid hourly rate');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSave(rate);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hourly rate');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      icon={<PoundSterling />}
      title="Hourly rate"
      description={`Used to calculate earnings for ${getMonthName(new Date(year, month - 1))}`}
      footer={
        <>
          <Button variant="secondary" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={isSubmitting}>
            <Save />
            {isSubmitting ? 'Saving…' : 'Save rate'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        <Field label="Rate per hour" htmlFor="hourlyRate" error={error}>
          <Input
            type="number"
            id="hourlyRate"
            inputMode="decimal"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            step="0.01"
            min="0"
            placeholder="0.00"
            leading="£"
            invalid={!!error}
            className="numeric h-12 text-lg"
            autoFocus
          />
        </Field>
      </form>
    </Dialog>
  );
};
